import { Box, Typography, IconButton, Divider, ClickAwayListener, Popper } from '@mui/material';
import { RoundedPaper } from '../../../atomic';
import { CalendarScheduleIcon } from '../../../../icons';
import { useState, useRef, useMemo } from 'react';
import { TableDataGrid } from '../../../compound/tableDataGrid';
import { useTranslation } from 'react-i18next';
import Util from '../../../../../misc/util';
import { useGetUpcomingBenefitSets } from '../../../../../queries/benefit/benefitSet';
interface BenefitSetUpdatesCellProps {
  employeeId: string;
}

export const BenefitSetUpdatesCell: React.FC<BenefitSetUpdatesCellProps> = ({ employeeId }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data: upcomingBenefitSets } = useGetUpcomingBenefitSets(employeeId);

  const rows = useMemo(() => {
    if (!upcomingBenefitSets) return [];

    const startDate = new Date();
    const filteredBenefitSets = upcomingBenefitSets.filter((benefitSet) => {
      if (!benefitSet.start_date) return false;
      return new Date(benefitSet.start_date) > startDate;
    });

    return filteredBenefitSets.map((item, index) => ({
      id: index + 1,
      ...item
    }));
  }, [upcomingBenefitSets]);

  const disabled = rows.length === 0;

  const handleOpen = (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelayedClose = () => {
    closeTimeoutRef.current = setTimeout(() => {
      handleClose();
    }, 50);
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  return (
    <>
      <IconButton
        onMouseOver={handleOpen}
        onMouseLeave={handleDelayedClose}
        disabled={disabled}
        sx={{ padding: 0 }}
      >
        <CalendarScheduleIcon
          sx={{
            color: 'primary.main',
            opacity: disabled ? '0.3' : '1'
          }}
        />
      </IconButton>

      <Popper
        id="benefitSet-overlay"
        open={!!anchorEl}
        anchorEl={anchorEl}
        placement="bottom-end"
        modifiers={[
          {
            fn: (popper) => {
              const y = popper.state.rects.reference.y;
              if (y >= window.innerHeight - 150 || y <= 150) {
                handleClose();
              }
            },
            phase: 'main',
            enabled: true,
            name: 'closeOutOffView'
          }
        ]}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleClose}
        className="z-50"
      >
        <ClickAwayListener onClickAway={handleClose}>
          <RoundedPaper className="flex flex-col w-[300px] max-h-[350px] border-none shadow-500">
            <Box className="p-4">
              <Typography fontWeight="bold" color="primary.main">
                {t('employee:PLANNED_BENEFIT_SET_ASSIGNMENTS')}
              </Typography>
            </Box>

            <Divider />

            {rows.length > 0 ? (
              <Box className="p-[6px] flex h-auto">
                <TableDataGrid
                  columns={[
                    {
                      field: 'name',
                      headerName: t('common:BENEFIT_SET'),
                      flex: 4,
                      valueGetter: ({ value }: { value: string }) => value
                    },
                    {
                      field: 'start_date',
                      headerName: t('common:DATE'),
                      type: 'date',
                      flex: 2,
                      valueGetter: ({ value }: { value: string }) => Util.formatDate(value)
                    }
                  ]}
                  rows={rows}
                  checkboxSelection={false}
                  autoHeight={false}
                  hideFooter
                  hideToolbar
                  rowHeight={45}
                  sx={{
                    height: rows.length * 52 + 56,
                    minHeight: '40px',
                    maxHeight: '270px',
                    width: '100%',
                    '& .MuiDataGrid-row': {
                      borderRadius: '8px'
                    },
                    '& .MuiDataGrid-row:nth-of-type(even)': {
                      backgroundColor: 'grey.50'
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: 'none'
                    }
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('form:NO_UPCOMING_UPDATES')}
                </Typography>
              </Box>
            )}
          </RoundedPaper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
